import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { AdCard } from "@/components/AdCard";
import { FilterSidebar } from "@/components/FilterSidebar";
import { InquiryDialog } from "@/components/InquiryDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Search, SlidersHorizontal } from "lucide-react";

export default function Home() {
  const { t } = useApp();
  const LIMIT = 24;
  const [ads, setAds] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [facets, setFacets] = useState({ categories: [], eras: [], ad_languages: [], brands: [], total: 0 });
  const [filters, setFilters] = useState({ category: "all", era: "all", ad_language: "English", brand: "all" });
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sort, setSort] = useState("random");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inquiryAd, setInquiryAd] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const seedRef = useRef(Math.random().toString(36).slice(2));

  useEffect(() => { api.get("/categories").then((r) => setFacets(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(id);
  }, [q]);

  const buildParams = useCallback((pageNum) => {
    const params = { page: pageNum, limit: LIMIT };
    if (filters.category !== "all") params.category = filters.category;
    if (filters.era !== "all") params.era = filters.era;
    if (filters.ad_language !== "all") params.ad_language = filters.ad_language;
    if (filters.brand !== "all") params.brand = filters.brand;
    if (debouncedQ) params.q = debouncedQ;
    if (sort !== "random") params.sort = sort;
    else params.seed = seedRef.current;
    return params;
  }, [filters, debouncedQ, sort]);

  // Reset and load first page whenever filters / search / sort change.
  useEffect(() => {
    seedRef.current = Math.random().toString(36).slice(2);
    setLoading(true);
    setPage(1);
    api.get("/ads", { params: buildParams(1) })
      .then((r) => {
        setAds(r.data.items);
        setTotal(r.data.total);
        setHasMore(r.data.has_more);
      })
      .catch(() => { setAds([]); setTotal(0); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [filters, debouncedQ, sort, buildParams]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const next = page + 1;
    api.get("/ads", { params: buildParams(next) })
      .then((r) => {
        setAds((prev) => [...prev, ...r.data.items]);
        setHasMore(r.data.has_more);
        setPage(next);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, page, buildParams]);

  // Auto-load more when the sentinel scrolls into view.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "600px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const openInquiry = (ad) => { setInquiryAd(ad); setDialogOpen(true); };

  const sortOptions = useMemo(() => ([
    ["random", t("sort_random")], ["newest", t("sort_newest")],
    ["price_asc", t("sort_price_asc")], ["price_desc", t("sort_price_desc")],
  ]), [t]);

  return (
    <div className="App">
      <Header onOpenFilters={() => setFiltersOpen(true)} />

      {/* Hero */}
      <section className="border-b border-[color:var(--c-border)] bg-[color:var(--c-surface)]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-14 sm:pt-10 sm:pb-20">
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {t("hero_title")}
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-[color:var(--c-ink2)] leading-relaxed">
            {t("hero_sub")}
          </p>
          <div className="mt-8 relative max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--c-ink2)]" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="rounded-none bg-[color:var(--c-bg)] border-[color:var(--c-ink)] h-14 pl-12 text-base"
            />
          </div>
        </div>
      </section>

      {/* Catalog */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 flex flex-col lg:flex-row gap-10">
        {/* Desktop sidebar */}
        <div className="hidden lg:block">
          <FilterSidebar facets={facets} filters={filters} setFilters={setFilters} />
        </div>

        <main className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <p className="text-sm text-[color:var(--c-ink2)]" data-testid="results-count">
              <span className="font-serif text-2xl text-[color:var(--c-ink)] mr-2">{total}</span> {t("results")}
            </p>
            <div className="flex items-center gap-3">
              {/* Mobile filters */}
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filters-trigger" className="lg:hidden inline-flex items-center gap-2 border border-[color:var(--c-ink)] px-4 py-2.5 text-xs uppercase tracking-widest font-semibold">
                    <SlidersHorizontal size={15} /> {t("filters")}
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-[color:var(--c-bg)] w-[85vw] max-w-sm overflow-y-auto pv-scroll">
                  <div className="mt-8">
                    <FilterSidebar facets={facets} filters={filters} setFilters={setFilters} onAfterSelect={() => setFiltersOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-[190px] h-11 rounded-none bg-[color:var(--c-surface)] border-[color:var(--c-border)] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {sortOptions.map(([v, l]) => (
                    <SelectItem key={v} value={v} data-testid={`sort-option-${v}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="pv-masonry columns-1 sm:columns-2 xl:columns-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="mb-7 aspect-[4/5] bg-[color:var(--c-surface)] animate-pulse border border-[color:var(--c-border)]" />
              ))}
            </div>
          ) : ads.length === 0 ? (
            <div data-testid="no-results" className="py-24 text-center text-[color:var(--c-ink2)]">
              <p className="font-serif text-3xl text-[color:var(--c-ink)]">{t("no_results")}</p>
            </div>
          ) : (
            <>
              <div className="pv-masonry columns-1 sm:columns-2 xl:columns-3">
                {ads.map((ad, i) => (
                  <AdCard key={ad.id} ad={ad} index={i} onInquire={openInquiry} />
                ))}
              </div>

              {/* Infinite-scroll sentinel + fallback button */}
              <div ref={sentinelRef} className="h-4" data-testid="scroll-sentinel" />
              {loadingMore && (
                <div className="pv-masonry columns-1 sm:columns-2 xl:columns-3 mt-7" data-testid="loading-more">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="mb-7 aspect-[4/5] bg-[color:var(--c-surface)] animate-pulse border border-[color:var(--c-border)]" />
                  ))}
                </div>
              )}
              {hasMore && !loadingMore && (
                <div className="flex justify-center mt-10">
                  <button
                    data-testid="load-more-button"
                    onClick={loadMore}
                    className="inline-flex items-center gap-2 border border-[color:var(--c-ink)] px-8 py-3.5 text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--c-ink)] hover:text-[color:var(--c-bg)] transition-colors"
                  >
                    {t("load_more")}
                  </button>
                </div>
              )}
              {!hasMore && ads.length > 0 && (
                <p className="text-center mt-10 text-xs uppercase tracking-widest text-[color:var(--c-ink2)]" data-testid="end-of-catalog">
                  {t("end_of_catalog")}
                </p>
              )}
            </>
          )}
        </main>
      </div>

      <InquiryDialog ad={inquiryAd} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
